import { BulletinItem } from '../bulletins/bulletinList/BulletinList';
import { BulletinSortDirection } from 'src/utils/bulletins';
import { BulletinsService } from 'src/services/bulletins.service';
import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { formatDate } from 'src/utils/helpers';
import { useLocalization } from 'src/utils/useLocalization';
import { useTranslation } from 'react-i18next';
import {
    LABEL_BULLETINS_SORT_ASCENDING,
    LABEL_BULLETINS_SORT_DESCENDING,
    LABEL_BULLETINS_SORT_TOGGLE,
} from 'src/localization/resouceKeys';

import './style.scss';

const DATE_ONLY_LENGTH = 10;
const DATE_START_INDEX = 0;
const DATE_TIME_SEPARATOR = 'T12:00:00';

type BulletinsLabels = ReturnType<typeof useLocalization>['bulletins'];

const getBulletinTypeLabels = (
    bulletins: BulletinsLabels,
): Record<number, string> => ({
    0: bulletins.LABEL_BULLETINS_TYPE_LEASE,
    1: bulletins.LABEL_BULLETINS_TYPE_RETAIL,
    2: bulletins.LABEL_BULLETINS_TYPE_BALLOON,
    3: bulletins.LABEL_BULLETINS_TYPE_MISCELLANEOUS,
    4: bulletins.LABEL_BULLETINS_TYPE_INFORMATIONAL,
    5: bulletins.LABEL_BULLETINS_TYPE_PROGRAMS,
});

type BulletinTableItem = BulletinItem & {
    bulletinNumber?: string;
    bulletinType?: number | string;
    bulletinId?: string;

    modifytime?: string;
    updatedTime?: string;
    lastModifiedTime?: string;

    /*
     * Use the URL returned by the API.
     * These aliases let us support the likely
     * backend response names without hardcoding
     * a fake document endpoint.
     */
    documentUrl?: string;
    bulletinUrl?: string;
    url?: string;
};

export type BulletinTableSortColumn = 'type' | 'startDate' | 'endDate';

export interface ITableContents {
    items: BulletinItem[];
    sortColumn: BulletinTableSortColumn;
    sortDirection: BulletinSortDirection;
    onSort: (
        column: BulletinTableSortColumn,
        direction?: BulletinSortDirection,
    ) => void;
}

export interface Props {
    data: BulletinItem;
}

const formatDateOnly = (value?: string): string => {
    if (!value) {
        return '';
    }

    if (value.includes('/')) {
        return formatDate(value);
    }

    const dateOnly = value.slice(DATE_START_INDEX, DATE_ONLY_LENGTH);

    return dateOnly ? formatDate(`${dateOnly}${DATE_TIME_SEPARATOR}`) : '';
};

const getBulletinTypeLabel = (
    bulletins: BulletinsLabels,
    bulletinType?: number | string,
): string => {
    if (
        bulletinType === undefined ||
        bulletinType === null ||
        bulletinType === ''
    ) {
        return '';
    }

    const typeLabels = getBulletinTypeLabels(bulletins);

    if (typeof bulletinType === 'number') {
        return typeLabels[bulletinType] ?? `${bulletinType}`;
    }

    const numericType = Number(bulletinType);

    if (!Number.isNaN(numericType)) {
        return typeLabels[numericType] ?? bulletinType;
    }

    return bulletinType;
};

const getModifyDate = (item: BulletinTableItem): string => {
    return item.modifytime ?? item.updatedTime ?? item.lastModifiedTime ?? '';
};

export const TableItem = ({ data }: Props) => {
    const { bulletins } = useLocalization();
    const item = data as BulletinTableItem;

    const {
        bulletinNumber,
        title,
        bulletinType,
        validFrom,
        validThrough,
        documentId,
    } = item;

    const modifyDate = getModifyDate(item);

    async function handlePdf(id: string) {
        try {
            const service = new BulletinsService();
            const res = await service.getBulletin(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            URL.revokeObjectURL(url);
        } catch (error) {
            console.log('Error descargando PDF:', error);
        }
    }

    return (
        <div className='bulletins-table-row'>
            <div className='table-row col-1'>
                <span className='list-label'>
                    {bulletins.LABEL_BULLETINS_COLUMN_NUMBER}
                </span>

                <div className='mobile-strong'>
                    <a
                        href='#'
                        onClick={() => {
                            handlePdf(documentId!);
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {bulletinNumber}
                    </a>
                </div>
            </div>

            <div className='table-row col-2'>
                <span className='list-label'>
                    {bulletins.LABEL_BULLETINS_COLUMN_TITLE}
                </span>

                <span className='label-value'>{title}</span>
            </div>

            <div className='table-row col-3'>
                <span className='list-label'>
                    {bulletins.LABEL_BULLETINS_TYPE}
                </span>

                <span>{getBulletinTypeLabel(bulletins, bulletinType)}</span>
            </div>

            <div className='table-row col-4'>
                <span className='list-label'>
                    {bulletins.LABEL_BULLETINS_COLUMN_START_DATE}
                </span>

                <span>{formatDateOnly(validFrom)}</span>
            </div>

            <div className='table-row col-5'>
                <span className='list-label'>
                    {bulletins.LABEL_BULLETINS_COLUMN_END_DATE}
                </span>

                <span>{formatDateOnly(validThrough)}</span>
            </div>

            <div className='table-row col-6'>
                <span className='list-label'>
                    {bulletins.LABEL_BULLETINS_COLUMN_MODIFY_DATE}
                </span>

                <span>{formatDateOnly(modifyDate)}</span>
            </div>
        </div>
    );
};

export const Table = ({
    items,
    sortColumn,
    sortDirection,
    onSort,
}: ITableContents) => {
    const { bulletins } = useLocalization();
    const { t } = useTranslation();
    const chevronColor = '#363b49';

    const sortableHeader = (
        column: BulletinTableSortColumn,
        label: string,
        colClass: string,
    ) => {
        const isActive = column === sortColumn;
        const isAscending = isActive && sortDirection === 'asc';

        return (
            <div className={`table-header ${colClass}`}>
                <span className='u-text-truncate'>{label}</span>

                <span className='table-header__order'>
                    <button
                        className='sort-control'
                        type='button'
                        onClick={() => onSort(column, 'asc')}
                        aria-label={t(
                            isActive
                                ? LABEL_BULLETINS_SORT_TOGGLE
                                : LABEL_BULLETINS_SORT_ASCENDING,
                            { column: label },
                        )}
                    >
                        <Chevron
                            className={`accordion__icon ${
                                isAscending ? 'rotate-down' : 'rotate-up'
                            }`}
                            width={10}
                            fill={chevronColor}
                        />
                    </button>

                    {!isActive && (
                        <button
                            className='sort-control'
                            type='button'
                            onClick={() => onSort(column, 'desc')}
                            aria-label={t(LABEL_BULLETINS_SORT_DESCENDING, {
                                column: label,
                            })}
                        >
                            <Chevron
                                className='accordion__icon rotate-down'
                                width={10}
                                fill={chevronColor}
                            />
                        </button>
                    )}
                </span>
            </div>
        );
    };

    return (
        <div className='bulletins-table'>
            <div className='bulletins-table-row header-row hidden-in-list'>
                <div className='table-header col-1'>
                    <span className='u-text-truncate'>
                        {bulletins.LABEL_BULLETINS_COLUMN_NUMBER}
                    </span>
                </div>

                <div className='table-header col-2'>
                    <span className='u-text-truncate'>
                        {bulletins.LABEL_BULLETINS_COLUMN_TITLE}
                    </span>
                </div>

                {sortableHeader(
                    'type',
                    bulletins.LABEL_BULLETINS_TYPE,
                    'col-3',
                )}

                {sortableHeader(
                    'startDate',
                    bulletins.LABEL_BULLETINS_COLUMN_START_DATE,
                    'col-4',
                )}

                {sortableHeader(
                    'endDate',
                    bulletins.LABEL_BULLETINS_COLUMN_END_DATE,
                    'col-5',
                )}

                <div className='table-header col-6'>
                    <span className='u-text-truncate'>
                        {bulletins.LABEL_BULLETINS_COLUMN_MODIFY_DATE}
                    </span>
                </div>
            </div>

            {items.map((item) => (
                <TableItem key={item.documentId} data={item} />
            ))}
        </div>
    );
};

export default Table;
